import type { ComponentProps } from 'react';
import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import type { AlignItemsProps, BorderRadiusProps, DisplayProps } from 'styled-system';
import { alignItems, borderRadius, display, themeGet } from 'styled-system';

import { Button } from 'common/components/Button';
import { Flex } from 'common/components/Flex';
import Icon from 'common/components/Icon';
import { CaptionBoldCB1, CaptionRegularCR1 } from 'common/components/Typography';
import { useOpenZipCodeDrawer } from 'common/components/ZipCodeDrawer/hooks';
import type { ZipCodeUpdateEvent } from 'common/components/ZipCodeForm/event';
import { ZipCodeFormPlacement } from 'common/components/ZipCodeForm/event';
import cookieStorage from 'common/CookieStorage';
import { EventBusEvent } from 'common/enums/EventBus';
import { useSubscriptionCallback } from 'common/hooks/subscription';
import { useLocalization } from 'common/providers/LocaleProvider';
import { trackZipCodeClick } from './tracking';

const Container = styled(Button)<DisplayProps & AlignItemsProps & BorderRadiusProps>`
  ${display}
  ${alignItems}
  ${borderRadius}

  &:hover div {
    color: ${themeGet('colors.sys.primary.text.hover')};
  }
  &:active div {
    color: ${themeGet('colors.sys.primary.text.active')};
  }
  &:active svg {
    fill: ${themeGet('colors.sys.primary.text.active')};
  }
`;

const containerStyles: ComponentProps<typeof Container> = {
  kind: 'bare',
  display: 'flex',
  alignItems: 'center',
  borderRadius: 'small',
};

const onZipCodeUpdate = () => {
  // We reload the page so all affected components would re-fetch the data.
  // As the future improvement, components could listen to "ZipCodeUpdate" event
  // and handle the internal update instead.
  window.location.reload();
};

const ZipCode = () => {
  const { t, r } = useLocalization();
  const openZipCodeDrawer = useOpenZipCodeDrawer(
    onZipCodeUpdate,
    ZipCodeFormPlacement.GlobalHeader
  );
  const [zipCode, setZipCode] = useState('-');
  const zipCodeLabel = zipCode ?? t('h24_enter_zip_code_short');

  // Update the zip code on the client-side, to avoid hydration error.
  // Can be improved by reading the cookie on server and passing down prop from Page app.
  useEffect(() => {
    setZipCode(cookieStorage.getZipCode());
  }, []);

  const handleClick = () => {
    trackZipCodeClick(zipCodeLabel);
    openZipCodeDrawer();
  };

  useSubscriptionCallback<ZipCodeUpdateEvent>(
    EventBusEvent.ZipCodeUpdate,
    ({ zipCode: newZipCode }) => {
      setZipCode(newZipCode);
    }
  );

  return (
    <Container onClick={handleClick} {...containerStyles} data-testid="zip-code-container">
      <Icon name="pin" size={32} />
      <Flex flexDirection={['row', null, 'column']} alignItems="flex-start">
        <CaptionRegularCR1 mr={['sp_4', null, 'sp_0']}>
          {r.t('h24_shipping_to_zip', { zipCode: ' ' })}
        </CaptionRegularCR1>
        <CaptionBoldCB1>{zipCodeLabel}</CaptionBoldCB1>
      </Flex>
    </Container>
  );
};

export default ZipCode;
